import { BorderRadius, CustomIcon, FeatureIcon, Shadow, SubduedIcon } from '../types/pricingPage';
import { enumKeys } from './typescript';

interface IconData {
    name: FeatureIcon | SubduedIcon;
    url: string;
}

function isCustomIcon(icon: FeatureIcon | SubduedIcon | CustomIcon): icon is CustomIcon {
    return (
        icon !== FeatureIcon.BOLD &&
        icon !== FeatureIcon.CIRCLE &&
        icon !== FeatureIcon.GREEN &&
        icon !== FeatureIcon.PURPLE &&
        icon !== FeatureIcon.SQUARE &&
        icon !== SubduedIcon.RED_X &&
        icon !== SubduedIcon.GREY_X
    );
}

export const iconToUrl = (iconName: FeatureIcon | SubduedIcon | CustomIcon, baseUrl?: string) =>
    isCustomIcon(iconName) ? iconName : `${baseUrl || ''}/images/feature-icon/${iconName}.png`;

export const FEATURE_ICONS: IconData[] = enumKeys(FeatureIcon).map((name) => ({
    name: FeatureIcon[name],
    url: iconToUrl(FeatureIcon[name]),
}));

export const SUBDUED_ICONS: IconData[] = enumKeys(SubduedIcon).map((name) => ({
    name: SubduedIcon[name],
    url: iconToUrl(SubduedIcon[name]),
}));

interface BorderRadiusData {
    name: BorderRadius | 'none';
    label: string;
    className: string;
}

export enum Corner {
    TOPLEFT = 'tl',
    TOPRIGHT = 'tr',
    BOTTOMLEFT = 'bl',
    BOTTOMRIGHT = 'br',
}

export const getBorderClassName = (borderRadius: BorderRadius | 'none', corner: Corner | '' = '') => {
    const cornerClass = corner ? `${corner}-` : '';
    if (borderRadius === BorderRadius.SMALL) {
        return `rounded-${cornerClass}sm`;
    } else if (borderRadius === BorderRadius.MEDIUM) {
        return `rounded-${cornerClass}md`;
    } else if (borderRadius === BorderRadius.LARGE) {
        return `rounded-${cornerClass}lg`;
    } else if (borderRadius === BorderRadius.EXTRALARGE) {
        return `rounded-${cornerClass}xl`;
    } else {
        return '';
    }
};

export const getBorderLabel = (borderRadius: BorderRadius | 'none') => {
    if (borderRadius === BorderRadius.SMALL) {
        return 'slightly round corners';
    } else if (borderRadius === BorderRadius.MEDIUM) {
        return 'round corners';
    } else if (borderRadius === BorderRadius.LARGE) {
        return 'very round corners';
    } else if (borderRadius === BorderRadius.EXTRALARGE) {
        return 'very very round corners';
    } else {
        return '';
    }
};

export const BORDER_RADIUS_OPTIONS: BorderRadiusData[] = [
    { name: 'none', label: 'square corners', className: getBorderClassName('none') },
    ...enumKeys(BorderRadius).map((name) => ({
        name: BorderRadius[name],
        label: getBorderLabel(BorderRadius[name]),
        className: getBorderClassName(BorderRadius[name]),
    })),
];

interface ShadowData {
    name: Shadow | 'none';
    label: string;
    className: string;
}

export const getShadowClassName = (shadow: Shadow | 'none') => {
    if (shadow === Shadow.SMALL) {
        return 'shadow-sm';
    } else if (shadow === Shadow.MEDIUM) {
        return 'shadow-md';
    } else if (shadow === Shadow.LARGE) {
        return 'shadow-lg';
    } else {
        return '';
    }
};

export const getShadowLabel = (shadow: Shadow | 'none') => {
    if (shadow === Shadow.SMALL) {
        return 'slight shadow';
    } else if (shadow === Shadow.MEDIUM) {
        return 'regular shadow';
    } else if (shadow === Shadow.LARGE) {
        return 'deep shadow';
    } else {
        return '';
    }
};

export const SHADOW_OPTIONS: ShadowData[] = [
    { name: 'none', label: 'no shadow', className: getShadowClassName('none') },
    ...enumKeys(Shadow).map((name) => ({
        name: Shadow[name],
        label: getShadowLabel(Shadow[name]),
        className: getShadowClassName(Shadow[name]),
    })),
];
